import { useDirectoryCreateRecord, useDirectoryGetSchemas } from 'api/requests';
import { PermissionType } from 'api/types/entity';
import { useServerErrorNotify } from 'hooks';
import { FormSubmit } from 'libs/form';
import { useTranslation } from 'libs/i18n';
import { ROUTES, useNavigate } from 'libs/navigation';
import { useNotify } from 'libs/notify';
import { usePermissions } from 'libs/permissions';
import { useFieldsConfig } from 'modules/directory/common/hooks/useFieldsConfig';
import { useValidationSchema } from 'modules/directory/common/hooks/useValidationSchema';

import { getCreateSubmitData } from '../helpers';

export const useCreateDirectory = (schemaId: string) => {
  const notify = useNotify();
  const { t, language } = useTranslation();
  const { checkPermissions } = usePermissions();
  const { navigate } = useNavigate();

  const { schemas: allSchemas, loading: loadingSchemas } =
    useDirectoryGetSchemas();
  const currentSchema = allSchemas?.find((schema) => schema.name === schemaId);

  const {
    createRecord,
    loading: submitLoading,
    error,
  } = useDirectoryCreateRecord();

  const fieldsConfig = useFieldsConfig({
    currentSchema,
    allSchemas,
  });

  const validationSchema = useValidationSchema(currentSchema);

  const isCanAdd = checkPermissions([PermissionType.Directory_UpdateOne]);

  const onSubmit: FormSubmit<{}> = async (values) => {
    if (currentSchema && isCanAdd) {
      const data = getCreateSubmitData(currentSchema, values);
      const result = await createRecord(data);
      navigate(ROUTES.directories.directory.directoryRecord.fullPath, {
        schemaId,
        directoryRecordId: result.data?.create?.data.id ?? null,
      });
      notify.info(t('directory.addDialog.successAdd'));
    }
  };

  useServerErrorNotify(error);

  return {
    fieldsConfig,
    validationSchema,
    submitLoading,
    onSubmit,
    directoryName: currentSchema?.label[language] || currentSchema?.label.ru,
    loadingSchemas: !allSchemas && loadingSchemas,
  };
};
